import { TILE, tileKey } from '../../shared/geo.js';
import { pointInPolygon } from '../../shared/clip.js';
import { footprint, compoundOf, compoundColliders } from './buildings.js';

// What the game asks the loaded tiles, apart from drawing them: which buildings are near a point,
// what surface a point is on, whether it is in water. Both streaming paths (whole tiles and
// worker-built cells) keep their tiles in a Map of { tx, tz, data, colliders, buckets, roadCells }
// and answer through here.

export const BUCKET = 32; // metres, collision lookup cell
export const ROAD_CELL = 16; // metres, road lookup cell

// The footprints of a tile's buildings in world coordinates, for collision.
export function buildingColliders(data, tx, tz, heightmap) {
  const out = [];
  for (let i = 0; i < data.b.length; i++) {
    const b = data.b[i];
    if (b.p.length < 6) continue;
    out.push(footprint(b, tx, tz, heightmap));
    // the wall round a compound blocks the way too, except at its gate
    const c = compoundOf(data, i, tx, tz);
    if (c) for (const piece of compoundColliders(c, tx, tz)) out.push(piece);
  }
  return out;
}

// Buckets the footprints so nearby ones are found without scanning the whole tile.
export function bucketColliders(colliders) {
  const buckets = new Map();
  for (const c of colliders) {
    for (let gz = Math.floor((c.minZ - 4) / BUCKET); gz <= Math.floor((c.maxZ + 4) / BUCKET); gz++) {
      for (let gx = Math.floor((c.minX - 4) / BUCKET); gx <= Math.floor((c.maxX + 4) / BUCKET); gx++) {
        const k = gx * 100003 + gz;
        let list = buckets.get(k);
        if (!list) buckets.set(k, (list = []));
        list.push(c);
      }
    }
  }
  return buckets;
}

// Road segments by cell, so vehicles can tell what they are driving on.
export function roadCells(data, tx, tz) {
  const cells = new Map();
  const ox = tx * TILE;
  const oz = tz * TILE;
  for (const r of data.r) {
    const half = r.w / 2 + 0.6;
    for (let i = 2; i < r.p.length; i += 2) {
      const seg = { ax: ox + r.p[i - 2] / 10, az: oz + r.p[i - 1] / 10, bx: ox + r.p[i] / 10, bz: oz + r.p[i + 1] / 10, half, s: r.s };
      const x0 = Math.floor((Math.min(seg.ax, seg.bx) - half) / ROAD_CELL);
      const x1 = Math.floor((Math.max(seg.ax, seg.bx) + half) / ROAD_CELL);
      const z0 = Math.floor((Math.min(seg.az, seg.bz) - half) / ROAD_CELL);
      const z1 = Math.floor((Math.max(seg.az, seg.bz) + half) / ROAD_CELL);
      for (let gz = z0; gz <= z1; gz++) {
        for (let gx = x0; gx <= x1; gx++) {
          const k = gx * 100003 + gz;
          let list = cells.get(k);
          if (!list) cells.set(k, (list = []));
          list.push(seg);
        }
      }
    }
  }
  return cells;
}

// Gives a tile record its lookup tables from its data (none if the tile is empty ground).
export function indexTile(t, heightmap) {
  if (!t.data) return;
  t.colliders = buildingColliders(t.data, t.tx, t.tz, heightmap);
  t.buckets = bucketColliders(t.colliders);
  t.roadCells = roadCells(t.data, t.tx, t.tz);
}

// What is under a point: 0 tarred road, 1 earth road, -1 open ground.
export function surfaceAt(tiles, x, z) {
  const t = tiles.get(tileKey(Math.floor(x / TILE), Math.floor(z / TILE)));
  const list = t && t.roadCells ? t.roadCells.get(Math.floor(x / ROAD_CELL) * 100003 + Math.floor(z / ROAD_CELL)) : null;
  if (!list) return -1;
  let best = -1;
  for (const s of list) {
    const ex = s.bx - s.ax;
    const ez = s.bz - s.az;
    let u = ((x - s.ax) * ex + (z - s.az) * ez) / (ex * ex + ez * ez || 1);
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    if (Math.hypot(x - s.ax - ex * u, z - s.az - ez * u) > s.half) continue;
    if (s.s === 0) return 0;
    best = 1;
  }
  return best;
}

// True if a point is in a river, stream or lake, or within margin metres of one.
export function wet(map, tiles, x, z, margin = 0) {
  // the sea: on a map that reaches the coast, ground at sea level is under water
  if (map.meta.height.min < 1 && map.heightmap.heightAt(x, z) < 0.5) return true;
  const t = tiles.get(tileKey(Math.floor(x / TILE), Math.floor(z / TILE)));
  if (!t || !t.data) return false;
  // tile data is in decimetres from the tile's corner
  const lx = (x - t.tx * TILE) * 10;
  const lz = (z - t.tz * TILE) * 10;
  for (const w of t.data.w) {
    const reach = (w.w / 2 + margin) * 10;
    const p = w.p;
    for (let i = 2; i < p.length; i += 2) {
      const ex = p[i] - p[i - 2];
      const ez = p[i + 1] - p[i - 1];
      let u = ((lx - p[i - 2]) * ex + (lz - p[i - 1]) * ez) / (ex * ex + ez * ez || 1);
      u = u < 0 ? 0 : u > 1 ? 1 : u;
      if (Math.hypot(lx - p[i - 2] - ex * u, lz - p[i - 1] - ez * u) < reach) return true;
    }
  }
  for (const a of t.data.a) if (pointInPolygon(lx, lz, a.p)) return true;
  return false;
}

// Building footprints near a point, for collision.
export function collidersNear(tiles, x, z, out) {
  out.length = 0;
  const cx = Math.floor(x / TILE);
  const cz = Math.floor(z / TILE);
  const k = Math.floor(x / BUCKET) * 100003 + Math.floor(z / BUCKET);
  // a building belongs to the tile of its centre, so it may reach in from a neighbour
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const t = tiles.get(tileKey(cx + dx, cz + dz));
      const list = t && t.buckets ? t.buckets.get(k) : null;
      if (!list) continue;
      for (const c of list) {
        if (x > c.minX - 4 && x < c.maxX + 4 && z > c.minZ - 4 && z < c.maxZ + 4) out.push(c);
      }
    }
  }
  return out;
}
